import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { preview12Month, money, FORM_GRID_2, FORM_GRID_3 } from "@/components/opportunities/shared";
import {
  NONE, fetchOppRefsInto, fetchProspectChildrenInto, buildOpportunityForm,
  buildOpportunityPayload, validateOpportunityForm, OPP_DIALOG_COPY,
} from "@/components/opportunities/opportunityForm";
import { CircleNotch } from "@phosphor-icons/react";
import { CurrencyInput } from "@/components/ui/currency-input";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

function Fld({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-sm border border-[#E4E4E7] p-4">
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-[#032852]">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function ActionRow({ loading, submitLabel, onClose, testId }) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="outline" className="rounded-sm" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
      <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid={testId}>
        {loading ? <CircleNotch size={18} className="animate-spin" /> : submitLabel}
      </Button>
    </div>
  );
}

// --- Focused form sections ---------------------------------------------------
function OwnerAndOriginSection({ form, set, refs, isAdmin, editing, userName }) {
  const openStages = refs.stages.filter((s) => !s.name.startsWith("Closed"));
  return (
    <div className={FORM_GRID_2}>
      {isAdmin && (
        <Fld label="Owner">
          <Select value={form.owner_id} onValueChange={set("owner_id")}>
            <SelectTrigger className="rounded-sm" data-testid="opp-owner"><SelectValue placeholder={userName} /></SelectTrigger>
            <SelectContent>{refs.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
      )}
      <Fld label="Lead source (how the contact originated)">
        <Select value={form.lead_source_id} onValueChange={set("lead_source_id")}>
          <SelectTrigger className="rounded-sm" data-testid="opp-lead-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>None</SelectItem>
            {refs.sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Fld>
      {!editing && (
        <Fld label="Starting stage">
          <Select value={form.pipeline_stage_id} onValueChange={set("pipeline_stage_id")}>
            <SelectTrigger className="rounded-sm" data-testid="opp-stage"><SelectValue placeholder="New Lead (default)" /></SelectTrigger>
            <SelectContent>{openStages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.probability}% probability</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-[#6D6D70]">Probability is a stage-based forecast estimate, not a guarantee.</p>
        </Fld>
      )}
    </div>
  );
}

function DivisionsSection({ form, toggleIn, divisions }) {
  return (
    <Fld label="Service division(s)">
      <div className={`${FORM_GRID_2} !gap-1.5`} data-testid="opp-divisions">
        {divisions.map((d) => (
          <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm text-[#09090B]">
            <Checkbox checked={form.service_division_ids.includes(d.id)}
              onCheckedChange={() => toggleIn("service_division_ids", d.id)}
              data-testid={`opp-division-${d.id}`} />
            {d.name}
          </label>
        ))}
      </div>
    </Fld>
  );
}

function PropertiesSection({ form, set, toggleIn, properties }) {
  return (
    <div className="rounded-sm border border-[#E4E4E7] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Included properties *</Label>
        <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#032852]">
          <Switch checked={form.all_properties} onCheckedChange={set("all_properties")} data-testid="opp-all-properties" />
          All properties
        </label>
      </div>
      <p className="mt-1 text-[11px] text-[#6D6D70]">Every opportunity must include at least one property, or cover all properties.</p>
      {!form.all_properties && (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {properties.length === 0 && <p className="col-span-2 text-xs text-[#6D6D70]">No properties on this organization yet — add one from the organization page first.</p>}
          {properties.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-[#09090B]">
              <Checkbox checked={form.property_ids.includes(p.id)}
                onCheckedChange={() => toggleIn("property_ids", p.id)}
                data-testid={`opp-property-${p.id}`} />
              {p.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DatesSection({ form, set }) {
  return (
    <div className={FORM_GRID_3}>
      <Fld label="Target close"><DatePicker value={form.target_close_date} onChange={set("target_close_date")} testId="opp-target-close" /></Fld>
      <Fld label="Bid due"><DatePicker value={form.bid_due_date} onChange={set("bid_due_date")} testId="opp-bid-due" /></Fld>
      <Fld label="Service start"><DatePicker value={form.service_start_date} onChange={set("service_start_date")} testId="opp-service-start" /></Fld>
    </div>
  );
}

function FinancialsSection({ form, set }) {
  const twelveMonth = preview12Month(form.monthly_recurring_revenue, form.one_time_revenue, form.contract_term_months);
  return (
    <>
      <div className={FORM_GRID_3}>
        <Fld label="Opportunity type">
          <Select value={form.opportunity_type} onValueChange={set("opportunity_type")}>
            <SelectTrigger className="rounded-sm" data-testid="opp-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contract">Contract (recurring / term)</SelectItem>
              <SelectItem value="tag">Tag (project / one-time work)</SelectItem>
            </SelectContent>
          </Select>
        </Fld>
        {form.opportunity_type === "tag" ? (
          <Fld label="Total sale value $"><CurrencyInput value={form.one_time_revenue} onChange={set("one_time_revenue")} testId="opp-one-time" /></Fld>
        ) : (
          <>
            <Fld label="Monthly recurring $"><CurrencyInput value={form.monthly_recurring_revenue} onChange={set("monthly_recurring_revenue")} testId="opp-mrr" /></Fld>
            <Fld label="Contract term (months)"><Input type="number" min="1" value={form.contract_term_months} onChange={set("contract_term_months")} className="rounded-sm" data-testid="opp-term" /></Fld>
          </>
        )}
      </div>
      <div className="flex items-center justify-between rounded-sm bg-[#032852]/5 px-3 py-2">
        <span className="text-xs font-bold uppercase tracking-wide text-[#032852]">{form.opportunity_type === "tag" ? "Total Sale Value" : "12-Month Contract Value"}</span>
        <span className="font-heading text-lg font-black text-[#032852]" data-testid="opp-12mo-preview">{money(twelveMonth)}</span>
      </div>
    </>
  );
}

// --- Dialog ------------------------------------------------------------------
// Create/edit an Opportunity. Parent remounts via key per target.
// presetProspectId: locks the organization (e.g. from the prospect detail page).
export default function OpportunityDialog({ open, opportunity, presetProspectId, presetProspectName, onClose, onDone }) {
  const { user, isAdmin } = useAuth();
  const editing = Boolean(opportunity);
  const copy = OPP_DIALOG_COPY[editing ? "edit" : "create"];
  const [refs, setRefs] = useState({ stages: [], sources: [], divisions: [], users: [], prospects: [] });
  const [children, setChildren] = useState({ contacts: [], properties: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => buildOpportunityForm(opportunity, presetProspectId));
  const guard = useUnsavedGuard([form]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggleIn = (k, id) => setForm((f) => ({
    ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
  }));

  const helpers = useRef({ fetchOppRefsInto, fetchProspectChildrenInto });
  const needProspects = !editing && !presetProspectId;
  const loadRefs = useCallback(() => helpers.current.fetchOppRefsInto(setRefs, needProspects),
    [helpers, setRefs, needProspects]);
  useEffect(() => { loadRefs(); }, [loadRefs]);
  const prospectId = form.prospect_id;
  const loadChildren = useCallback(() => helpers.current.fetchProspectChildrenInto(setChildren, prospectId),
    [helpers, setChildren, prospectId]);
  useEffect(() => { loadChildren(); }, [loadChildren]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // both action rows submit the same form — never twice
    const validationError = validateOpportunityForm(form, editing, children.properties.length);
    if (validationError) return toast.error(validationError);
    // Stage-pricing rule (mirrors backend): Proposal or beyond needs real pricing.
    // Contract = positive monthly recurring; Tag = positive one-time sale value.
    const stage = editing
      ? { order: opportunity.stage_order, name: opportunity.stage_name }
      : refs.stages.find((s) => s.id === form.pipeline_stage_id) || { order: 0 };
    if ((stage.order ?? 0) >= 3 && stage.name !== "Closed - Lost") {
      if (form.opportunity_type === "tag" && !(Number(form.one_time_revenue) > 0)) {
        return toast.error("A Tag opportunity needs a positive one-time sale value to reach the Proposal stage or beyond");
      }
      if (form.opportunity_type !== "tag" && !(Number(form.monthly_recurring_revenue) > 0)) {
        return toast.error("A contract opportunity needs a positive monthly recurring amount to reach the Proposal stage or beyond — earlier-stage drafts stay marked \"Pricing pending\"");
      }
    }
    setLoading(true);
    try {
      const body = buildOpportunityPayload(form, isAdmin, editing);
      if (editing) {
        await api.patch(`/opportunities/${opportunity.id}`, { ...body, version: opportunity.version });
      } else {
        await api.post("/opportunities", body);
      }
      toast.success(copy.success);
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent {...guard.guardProps} className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="opportunity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <ActionRow loading={loading} submitLabel={copy.submit} onClose={onClose} testId="submit-opportunity-top" />
          <Section title="Organization & Contact">
            <div className="grid gap-4 sm:grid-cols-2">
              <Fld label="Opportunity name" required>
                <Input required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="opp-name" placeholder='e.g. "HQ Tower janitorial contract"' />
              </Fld>
              {needProspects ? (
                <Fld label="Organization" required>
                  <Select value={form.prospect_id} onValueChange={set("prospect_id")} required>
                    <SelectTrigger className="rounded-sm" data-testid="opp-prospect"><SelectValue placeholder="Select organization" /></SelectTrigger>
                    <SelectContent>
                      {refs.prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.organization_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Fld>
              ) : !editing ? (
                <Fld label="Organization">
                  <div className="flex h-9 items-center rounded-sm border border-[#E4E4E7] bg-[#F4F4F5] px-3 text-sm font-semibold text-[#032852]" data-testid="opp-locked-org">
                    {presetProspectName || "This organization"}
                    <span className="ml-2 text-[10px] font-bold uppercase text-[#6D6D70]">locked</span>
                  </div>
                </Fld>
              ) : null}
              <Fld label="Primary contact">
                <Select value={form.primary_contact_id} onValueChange={set("primary_contact_id")}>
                  <SelectTrigger className="rounded-sm" data-testid="opp-contact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {children.contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Fld>
            </div>
          </Section>
          <Section title="Sales Stage & Ownership">
            <OwnerAndOriginSection form={form} set={set} refs={refs} isAdmin={isAdmin} editing={editing} userName={user?.name} />
          </Section>
          <Section title="Service Scope & Locations">
            <DivisionsSection form={form} toggleIn={toggleIn} divisions={refs.divisions} />
            <PropertiesSection form={form} set={set} toggleIn={toggleIn} properties={children.properties} />
          </Section>
          <Section title="Pricing & Dates">
            <FinancialsSection form={form} set={set} />
            <DatesSection form={form} set={set} />
          </Section>
          <Section title="Credit & Commission">
            <div className="grid gap-4 sm:grid-cols-2">
              <Fld label="Quota Attribution (Lead Origin)">
                <Select value={form.origin_classification || ""} onValueChange={set("origin_classification")}>
                  <SelectTrigger className="rounded-sm" data-testid="opp-origin"><SelectValue placeholder="Select before proposal stage" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shirley_originated">Self-Originated (Owner-Generated) — 100% quota credit</SelectItem>
                    <SelectItem value="company_assigned">Company/Owner-Assigned (50% quota credit)</SelectItem>
                    <SelectItem value="tag_work">Tag Work (tracked separately)</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              <Fld label="Commission category (pay plan)">
                <Select value={form.commission_category || NONE}
                  onValueChange={(v) => set("commission_category")(v === NONE ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="opp-commission-category"><SelectValue placeholder="Needs classification" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Needs classification (no commission generates)</SelectItem>
                    <SelectItem value="fully_developed">Fully Developed Sale — 10% first month, then 1% months 2–12</SelectItem>
                    <SelectItem value="assisted">Assisted Sale — 7% first month, then 1% months 2–12</SelectItem>
                    <SelectItem value="tag_work">Tag Work — 10% one-time</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
            </div>
            {form.origin_classification === "shirley_originated" && (
              <Fld label="Origination justification" required>
                <Textarea required value={form.origin_justification} onChange={set("origin_justification")} rows={2}
                  placeholder="Document prospect development, decision-maker access, follow-up and proposal contribution"
                  className="rounded-sm" data-testid="opp-origin-justification" />
              </Fld>
            )}
            <p className="text-[11px] text-[#6D6D70]">
              Lead Source = how the contact originated. Quota Attribution (Lead Origin) = sales-credit
              classification — Self-Originated means the opportunity owner generated the lead (100% credit).
              Commission Category = pay plan (independent — changing it never changes quota credit).
              Eligible renewal pays a one-time 5% of one month's billing.
            </p>
          </Section>
          <Fld label="Notes"><Textarea value={form.closing_notes} onChange={set("closing_notes")} rows={2} className="rounded-sm" data-testid="opp-notes" /></Fld>
          <DialogFooter>
            <ActionRow loading={loading} submitLabel={copy.submit} onClose={onClose} testId="submit-opportunity" />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
